// src/components/OrderRequirements/components/CustomerAccordion.jsx
// ============================================================================
// BLOCK ORUI.ACC.0 — Customer accordion (parent container)
// ----------------------------------------------------------------------------
// RESPONSIBILITIES:
//  - Shows customer header (facility display line)
//  - Lazy-loads queue rows on first expand
//  - Holds editable state for order_qty and auto-buckets rows based on qty
//  - Submits ledger (submit-ledger) with REQUIRED customer_no + customer_name
// ============================================================================

import React from 'react';
import PropTypes from 'prop-types';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import axios from '../../../axiosClient.js';
import { resolveSugarLookupShipToCode } from '../../OrderEntry/utils/directShipRules.js';
import {
  fetchOrderRequirementQueue,
  submitOrderRequirementLedger,
  submitOrderRequirementToOrderEntry,
} from '../api/orderRequirementsApi.js';
import { buildSugarNoteTextForCustomer } from '../sugarNoteUtils.js';
import OrderRequirementTable from './OrderRequirementTable.jsx';

// LOCAL HELPER: Converts value to integer, returns 0 if invalid
// BLOCK ORUI.HELPER.1 — Conversion of input to integer
// ----------------------------------------------------------------------------
// This function ensures that values are treated as integers. If invalid, it returns 0.
// ============================================================================

const toInt = (v) => {
  if (v == null) return 0;
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return 0;
    return Math.max(0, Math.trunc(v));
  }

  const s = String(v).trim();
  if (!s) return 0;

  // Accept formatted values like "$12", "1,200", "(25)" and "25)".
  const isParenNegative = /^\(.*\)$/.test(s);
  const hasTrailingParen = /\)$/.test(s);
  const cleaned = s
    .replace(/[,$]/g, '')
    .replace(/[()]/g, '')
    .replace(/[^\d.+-]/g, '');

  const n = Number(cleaned);
  if (!Number.isFinite(n)) return 0;

  const signed = isParenNegative || hasTrailingParen ? -Math.abs(n) : n;
  return Math.max(0, Math.trunc(signed));
};

// LOCAL HELPER: Safe string trimming
// BLOCK ORUI.HELPER.2 — Trimming spaces from input string
// ----------------------------------------------------------------------------
// Trims spaces from strings, ensuring there are no unwanted spaces or nulls.
// ============================================================================

function safeTrim(value) {
  return String(value ?? '').trim();
}

function normalizeSugarCustomerNo(value) {
  return safeTrim(value)
    .replace(/^(?:00|01)-/i, '')
    .toUpperCase();
}

// BLOCK ORUI.ACC.1 — Split rows into "Need" and "Watch" buckets
// ----------------------------------------------------------------------------
// The rows are divided into two categories:
// "Need to Order" (order_qty > 0) and "Watch List" (order_qty = 0).
// This is used to organize orders that require attention and those to be watched.
// ============================================================================

function splitBuckets(rows) {
  const need = [];
  const watch = [];
  for (const r of rows) {
    const qty = toInt(r.order_qty);
    if (qty > 0) need.push({ ...r, order_qty: qty });
    else watch.push({ ...r, order_qty: 0 });
  }
  return { need, watch };
}

// BLOCK ORUI.ACC.2 — Facility line formatting for customer details
// ----------------------------------------------------------------------------
// Combines customer information such as name, address, city, state, and zip code.
// This function ensures the facility's address is displayed properly.
// ============================================================================

function formatFacilityLine(customer) {
  const name = safeTrim(customer.customer_name);
  const street = safeTrim(customer.facility_address);
  const city = safeTrim(customer.ship_to_city);
  const state = safeTrim(customer.ship_to_state);
  const zip = safeTrim(customer.ship_to_zip);

  const cityStateZip =
    city || state || zip
      ? `${city}${city && state ? ', ' : ''}${state}${zip ? ` ${zip}` : ''}`
      : '';

  const address = [street, cityStateZip].filter(Boolean).join(', ');

  if (name && address) return `${name} — ${address}`;
  return name || address || '';
}

function formatShipToAddress(shipTo) {
  const street = safeTrim(shipTo?.facility_address);
  const city = safeTrim(shipTo?.ship_to_city);
  const state = safeTrim(shipTo?.ship_to_state);
  const zip = safeTrim(shipTo?.ship_to_zip);

  const cityStateZip =
    city || state || zip
      ? `${city}${city && state ? ', ' : ''}${state}${zip ? ` ${zip}` : ''}`
      : '';

  return [street, cityStateZip].filter(Boolean).join(', ');
}

function getShipToOptions(customer) {
  const listed = Array.isArray(customer?.ship_to_addresses)
    ? customer.ship_to_addresses
    : Array.isArray(customer?.shipToAddresses)
      ? customer.shipToAddresses
      : [];

  const normalized = listed
    .map((entry) => ({
      facility_address: safeTrim(entry?.facility_address ?? entry?.facilityAddress),
      ship_to_city: safeTrim(entry?.ship_to_city ?? entry?.shipToCity),
      ship_to_state: safeTrim(entry?.ship_to_state ?? entry?.shipToState),
      ship_to_zip: safeTrim(entry?.ship_to_zip ?? entry?.shipToZip),
      ship_to_code: safeTrim(entry?.ship_to_code ?? entry?.shipToCode),
    }))
    .filter((entry) => entry.ship_to_code && entry.ship_to_code.toUpperCase() !== 'P');

  if (normalized.length > 0) {
    return normalized.sort((a, b) => {
      const left = a.ship_to_code.toUpperCase();
      const right = b.ship_to_code.toUpperCase();
      if (left === '1') return -1;
      if (right === '1') return 1;

      const leftNumeric = /^\d+$/.test(left);
      const rightNumeric = /^\d+$/.test(right);
      if (leftNumeric && rightNumeric) return Number(left) - Number(right);
      if (leftNumeric !== rightNumeric) return leftNumeric ? -1 : 1;
      return left.localeCompare(right);
    });
  }

  const fallbackCode = safeTrim(customer?.ship_to_code ?? customer?.shipToCode);
  if (!fallbackCode || fallbackCode.toUpperCase() === 'P') return [];

  return [
    {
      facility_address: safeTrim(customer?.facility_address ?? customer?.facilityAddress),
      ship_to_city: safeTrim(customer?.ship_to_city ?? customer?.shipToCity),
      ship_to_state: safeTrim(customer?.ship_to_state ?? customer?.shipToState),
      ship_to_zip: safeTrim(customer?.ship_to_zip ?? customer?.shipToZip),
      ship_to_code: fallbackCode,
    },
  ];
}

// ============================================================================
// BLOCK ORUI.ACC.3 — CustomerAccordion Component (Accordion UI)
// ----------------------------------------------------------------------------
// This component displays the customer's accordion and handles the logic for
// displaying order requirements (Need to Order vs Watch List), submitting the ledger,
// and updating the quantity for each order.
// ============================================================================

export default function CustomerAccordion({
  customer,
  defaultExpanded,
  updateCustomerStatus,
  notify,
}) {
  const [expanded, setExpanded] = React.useState(Boolean(defaultExpanded)); // Accordion state
  const [loading, setLoading] = React.useState(false); // Loading indicator state
  const [loadedOnce, setLoadedOnce] = React.useState(false); // Check if data is loaded
  const [error, setError] = React.useState(''); // Error handling
  const [needRows, setNeedRows] = React.useState([]); // Rows that need to be ordered
  const [watchRows, setWatchRows] = React.useState([]); // Rows to be watched
  const [watchListExpanded, setWatchListExpanded] = React.useState(false);
  const [notes, setNotes] = React.useState(''); // Customer notes
  const [submitting, setSubmitting] = React.useState(false); // Submit state
  const [lastBatchId, setLastBatchId] = React.useState(''); // ID of the last batch submitted

  const shipToOptions = React.useMemo(() => getShipToOptions(customer), [customer]);
  const [selectedShipToCode, setSelectedShipToCode] = React.useState(() =>
    safeTrim(shipToOptions[0]?.ship_to_code || customer?.ship_to_code || customer?.shipToCode)
  );

  const selectedShipTo =
    shipToOptions.find((entry) => safeTrim(entry.ship_to_code) === safeTrim(selectedShipToCode)) ||
    shipToOptions[0] ||
    null;

  React.useEffect(() => {
    if (!shipToOptions.length) {
      setSelectedShipToCode('');
      return;
    }

    const nextCode = safeTrim(shipToOptions[0].ship_to_code);
    if (
      !selectedShipToCode ||
      !shipToOptions.some((entry) => safeTrim(entry.ship_to_code) === safeTrim(selectedShipToCode))
    ) {
      setSelectedShipToCode(nextCode);
    }
  }, [selectedShipToCode, shipToOptions]);

  // BLOCK ORUI.ACC.4 — Load customer queue rows
  // ----------------------------------------------------------------------------
  const load = React.useCallback(async () => {
    if (loading || loadedOnce) return;

    setLoading(true);
    setError('');

    try {
      const data = await fetchOrderRequirementQueue(customer.customer_no);

      // Normalize and split into buckets
      const needRaw = Array.isArray(data?.need_to_order)
        ? data.need_to_order
        : Array.isArray(data?.needToOrder)
          ? data.needToOrder
          : [];
      const watchRaw = Array.isArray(data?.watch_list)
        ? data.watch_list
        : Array.isArray(data?.watchList)
          ? data.watchList
          : [];

      const combined = [...needRaw, ...watchRaw];

      const normalizedRows = combined.map((row) => {
        const submittedQty = toInt(row.submitted_qty ?? row.submittedQty);
        const estOnShelf = toInt(row.est_on_shelf ?? row.estOnShelf);
        const rawOrderQty = row.order_qty ?? row.orderQty ?? row.qty ?? row.amount;
        const orderQty = Math.max(0, toInt(rawOrderQty) - submittedQty);
        const lastOrderDate =
          row.last_order_date ?? row.lastOrderDate ?? row.submitted_at ?? row.submittedAt ?? null;

        return {
          ...row,
          submitted_qty: submittedQty,
          est_on_shelf: estOnShelf + submittedQty,
          order_qty: orderQty,
          last_order_date: lastOrderDate,
        };
      });

      const buckets = splitBuckets(normalizedRows);

      setNeedRows(buckets.need);
      setWatchRows(buckets.watch);

      if (buckets.need.length === 0) {
        updateCustomerStatus(customer.customer_no, 'noPending');
      } else {
        updateCustomerStatus(customer.customer_no, 'pending');
      }

      setLoadedOnce(true);
    } catch (e) {
      setError(e?.message || 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, [customer.customer_no, loading, loadedOnce, updateCustomerStatus]);

  // Trigger load on expand
  React.useEffect(() => {
    if (expanded) load();
  }, [expanded, load]);

  React.useEffect(() => {
    if (expanded) setWatchListExpanded(false);
  }, [expanded]);

  // BLOCK ORUI.ACC.5 — Handle quantity change
  // ----------------------------------------------------------------------------
  const handleQtyChange = React.useCallback(
    (row, raw) => {
      const qty = toInt(raw);

      // Remove row from both lists
      const key = `${row.itemno}::${row.lineno}`;
      const nextNeed = needRows.filter((r) => `${r.itemno}::${r.lineno}` !== key);
      const nextWatch = watchRows.filter((r) => `${r.itemno}::${r.lineno}` !== key);

      const nextRow = { ...row, order_qty: qty };

      if (qty > 0) nextNeed.push(nextRow);
      else nextWatch.push(nextRow);

      // Keep stable ordering: itemno asc, lineno asc
      const sorter = (a, b) =>
        String(a.itemno).localeCompare(String(b.itemno)) || Number(a.lineno) - Number(b.lineno);

      nextNeed.sort(sorter);
      nextWatch.sort(sorter);

      setNeedRows(nextNeed);
      setWatchRows(nextWatch);
    },
    [needRows, watchRows]
  );

  // BLOCK ORUI.ACC.6 — Submit ledger
  // ----------------------------------------------------------------------------
  // Submit flow:
  //  1) Record rows into Order Requirements ledger
  //  2) Build Order Entry payload from submitted rows
  //  3) Send to submit-order.js with hard-coded defaults required by TMS flow
  //
  // REQUIRED TMS DEFAULTS:
  //  - company   = TMS
  //  - termsCode = 02
  //  - shipVia   = FDX-GND
  //
  // IMPORTANT:
  // - submit-order.js maps:
  //     terms     <= payload.termsCode
  //     shipterms <= payload.shipVia
  // - So these MUST be populated here or they will arrive blank downstream.
  // ============================================================================
  const submit = React.useCallback(async () => {
    setSubmitting(true);
    setError('');
    setLastBatchId('');

    try {
      const submittedRows = needRows.filter((r) => toInt(r.order_qty) > 0);
      const firstSubmittedRow = submittedRows[0] || {};

      // ------------------------------------------------------------------------
      // BLOCK ORUI.ACC.6.1 — Derive submit header defaults
      // ----------------------------------------------------------------------------
      // Company is hard-coded per your TMS requirement.
      // Terms + ship via are also hard-coded so submit-order.js receives:
      //   terms     = 02
      //   shipterms = FDX-GND
      // ------------------------------------------------------------------------
      const derivedCompany = 'TMS';
      const derivedTermsCode = '02';
      const derivedShipVia = 'FDX-GND';

      const items = submittedRows.map((r) => ({
        itemno: r.itemno,
        lineno: r.lineno,
        submitted_qty: toInt(r.order_qty),
      }));

      if (items.length === 0) {
        notify?.('No items with quantity greater than 0 to submit.', 'warning');
        return;
      }

      // ------------------------------------------------------------------------
      // BLOCK ORUI.ACC.6.2 — Record OR ledger rows first
      // ------------------------------------------------------------------------
      const shipToCode = safeTrim(
        selectedShipTo?.ship_to_code ||
          selectedShipTo?.shipToCode ||
          firstSubmittedRow.ship_to_code ||
          firstSubmittedRow.shipToCode ||
          customer.ship_to_code ||
          customer.shipToCode
      );

      const shipAddress1 = safeTrim(
        selectedShipTo?.facility_address ||
          selectedShipTo?.facilityAddress ||
          customer.facility_address
      );
      const shipCity = safeTrim(
        selectedShipTo?.ship_to_city || selectedShipTo?.shipToCity || customer.ship_to_city
      );
      const shipState = safeTrim(
        selectedShipTo?.ship_to_state || selectedShipTo?.shipToState || customer.ship_to_state
      );
      const shipZip = safeTrim(
        selectedShipTo?.ship_to_zip || selectedShipTo?.shipToZip || customer.ship_to_zip
      );

      const res = await submitOrderRequirementLedger({
        company: derivedCompany,
        customerNo: customer.customer_no,
        customerName: customer.customer_name,
        facilityAddress: shipAddress1 || customer.facility_address,
        shipToCode,
        notes,
        items,
      });

      if (!res.ok) throw new Error('Ledger submit failed');

      setLastBatchId(res.batchId);

      let orderEntryError = null;

      try {
        // ----------------------------------------------------------------------
        // BLOCK ORUI.ACC.6.3 — Build Order Entry payload
        // ----------------------------------------------------------------------------
        // submit-order.js expects:
        //  - customerNo
        //  - customerName
        //  - shipToCode
        //  - poNumber
        //  - shipVia      -> becomes legacy shipterms
        //  - termsCode    -> becomes legacy terms
        // ----------------------------------------------------------------------
        const poSeed = safeTrim(res.batchId)
          .replace(/[^A-Za-z0-9]/g, '')
          .slice(0, 12);

        const poNumber = safeTrim(res.derivedPoNumber) || `OR-${poSeed || Date.now()}`;

        if (!shipToCode) {
          throw new Error('Missing ship-to code for Order Entry submit.');
        }

        const orderEntryPayload = {
          company: derivedCompany,
          customerNo: customer.customer_no,
          customerName: safeTrim(firstSubmittedRow.customer_name) || customer.customer_name,
          shipToCode,
          shipAddress1,
          city: shipCity,
          state: shipState,
          zip: shipZip,

          // --------------------------------------------------------------------
          // BLOCK ORUI.ACC.6.4 — Required submit-order.js header fields
          // ----------------------------------------------------------------------------
          // These feed directly into legacy rows:
          //   terms     <= termsCode
          //   shipterms <= shipVia
          // --------------------------------------------------------------------
          poNumber,
          termsCode: derivedTermsCode,
          shipVia: derivedShipVia,

          // Keep freight as your current OR flow expects.
          frtPay: 'FREE',

          lines: submittedRows.map((row) => ({
            partNo: safeTrim(row.itemno),
            qty: String(toInt(row.order_qty)),
            uom: safeTrim(row.uom) || 'EA',
            price: '0.00',
            specialOrderType: safeTrim(row.special_order_type || row.specialOrderType),
            customerNote: safeTrim(row.customer_note || row.customerNote),
          })),
        };

        await submitOrderRequirementToOrderEntry(orderEntryPayload);
      } catch (e) {
        orderEntryError = e;
      }

      // ------------------------------------------------------------------------
      // BLOCK ORUI.ACC.6.5 — Notify submit result
      // ------------------------------------------------------------------------
      if (orderEntryError) {
        const failedStep = safeTrim(orderEntryError?.step);
        const entryMessage = safeTrim(orderEntryError?.message) || 'Order Entry submit failed.';

        if (failedStep === 'build') {
          notify?.(`Ledger submitted. Order Entry build failed: ${entryMessage}`, 'warning');
        } else if (failedStep === 'legacy-insert') {
          notify?.(`Ledger submitted. Legacy insert failed: ${entryMessage}`, 'warning');
        } else {
          notify?.(`Ledger submitted. ${entryMessage}`, 'warning');
        }
      } else {
        notify?.('Order submitted to ledger and Order Entry.', 'success');
      }

      // ------------------------------------------------------------------------
      // BLOCK ORUI.ACC.6.6 — Move submitted rows to watch list
      // ------------------------------------------------------------------------
      const nowIso = new Date().toISOString();

      const updatedNeedRows = needRows.map((row) => {
        const matchingItem = items.find(
          (item) => item.itemno === row.itemno && item.lineno === row.lineno
        );

        if (!matchingItem) return row;

        return {
          ...row,
          order_qty: 0,
          est_on_shelf: toInt(row.est_on_shelf) + toInt(matchingItem.submitted_qty),
          last_order_date: row.last_order_date || nowIso,
        };
      });

      const remainingNeedRows = updatedNeedRows.filter((row) => row.order_qty > 0);
      const movedToWatchRows = updatedNeedRows.filter((row) => row.order_qty === 0);
      const updatedWatchRows = [...watchRows, ...movedToWatchRows];

      setWatchRows(updatedWatchRows);
      setNeedRows(remainingNeedRows);

      if (remainingNeedRows.length === 0) {
        updateCustomerStatus(customer.customer_no, 'noPending');
      } else {
        updateCustomerStatus(customer.customer_no, 'pending');
      }
    } catch (e) {
      const message = e?.message || 'Submit failed';
      setError(message);
      notify?.(message, 'error');
    } finally {
      setSubmitting(false);
    }
  }, [customer, needRows, notes, watchRows, selectedShipTo, updateCustomerStatus, notify]);

  const submitCount = needRows.filter((r) => toInt(r.order_qty) > 0).length;

  const handleSugarNote = React.useCallback(async () => {
    const rowsToSubmit = needRows.filter((r) => toInt(r.order_qty) > 0);
    if (rowsToSubmit.length === 0) {
      notify?.('No items with quantity greater than 0 to send to Sugar.', 'warning');
      return;
    }

    const noteText = buildSugarNoteTextForCustomer({
      customer,
      rows: rowsToSubmit,
      shipToCode: selectedShipToCode,
    });

    const company = 'TMS';
    const customerNo = normalizeSugarCustomerNo(customer?.customer_no ?? customer?.customerNo);
    const shipToBase = safeTrim(
      selectedShipToCode || customer?.ship_to_code || customer?.shipToCode || '1'
    );
    const shipToLookup = resolveSugarLookupShipToCode(company, shipToBase, shipToBase);

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(noteText);
      }

      const { data: sugarRes } = await axios.get('/api/order-entry/sugar-account', {
        params: {
          company,
          customerNo,
          shipToCode: shipToLookup,
        },
      });

      const idC = safeTrim(sugarRes?.id_c || sugarRes?.data?.id_c);
      if (!idC) {
        notify?.('Could not find a Sugar account for this customer.', 'warning');
        window.open('https://sugar.tzmedical.com/#Accounts', '_blank');
        return;
      }

      const sugarUrl = `https://sugar.tzmedical.com/#Accounts/${idC}`;
      window.open(sugarUrl, '_blank');
      notify?.('Sugar note copied to clipboard and Sugar opened.', 'success');
    } catch (error) {
      notify?.(`Sugar note failed: ${error?.message || 'Unknown error'}`, 'error');
    }
  }, [customer, needRows, notify, selectedShipToCode]);

  // ============================================================================
  // BLOCK ORUI.ACC.7 — Render the Accordion UI for Each Customer
  // ----------------------------------------------------------------------------
  // This is the final rendering part of the accordion UI, where customer data
  // is displayed along with order requirements and actions.
  // ============================================================================

  return (
    <div>
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
        disableGutters
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ width: '100%' }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                userSelect: 'text',
                WebkitUserSelect: 'text',
                MozUserSelect: 'text',
                cursor: 'text',
              }}
            >
              {customer.customer_no} — {formatFacilityLine(customer)}
            </Typography>

            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleSugarNote}
                disabled={submitCount === 0}
              >
                Sugar
              </Button>

              {shipToOptions.length > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Ship-to:
                  </Typography>
                  <TextField
                    select
                    size="small"
                    value={selectedShipToCode}
                    onChange={(event) => setSelectedShipToCode(event.target.value)}
                    sx={{ minWidth: 120 }}
                    SelectProps={{
                      native: false,
                    }}
                  >
                    {shipToOptions.map((option) => (
                      <MenuItem key={option.ship_to_code} value={option.ship_to_code}>
                        {option.ship_to_code} — {formatShipToAddress(option)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              )}
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          {error && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && loadedOnce && (
            <>
              {/* Need to order table */}
              <OrderRequirementTable
                title="Need to order"
                rows={needRows}
                onQtyChange={handleQtyChange}
                dense
              />

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="text"
                  onClick={() => setWatchListExpanded((prev) => !prev)}
                  endIcon={
                    <ExpandMoreIcon
                      sx={{
                        transform: watchListExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                      }}
                    />
                  }
                  sx={{ px: 0 }}
                >
                  {`Watch List items (${watchRows.length})`}
                </Button>

                <Collapse in={watchListExpanded} unmountOnExit>
                  <Box sx={{ mt: 1 }}>
                    <OrderRequirementTable
                      title="Watch List items"
                      rows={watchRows}
                      onQtyChange={handleQtyChange}
                      dense
                    />
                  </Box>
                </Collapse>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <TextField
                  fullWidth
                  size="small"
                  label="Notes (optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
                <Button
                  variant="contained"
                  disabled={submitting || submitCount === 0}
                  onClick={submit}
                  sx={{ minWidth: 180 }}
                >
                  {submitting ? 'Submitting…' : `Submit Order (${submitCount})`}
                </Button>
              </Stack>

              {lastBatchId && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="success">
                    Submitted ledger batch: <strong>{lastBatchId}</strong>
                  </Alert>
                </Box>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  );
}

CustomerAccordion.propTypes = {
  customer: PropTypes.shape({
    customer_no: PropTypes.string.isRequired,
    customer_name: PropTypes.string.isRequired,
    facility_address: PropTypes.string,
    ship_to_city: PropTypes.string,
    ship_to_state: PropTypes.string,
    ship_to_zip: PropTypes.string,
    ship_to_code: PropTypes.string,
  }).isRequired,
  defaultExpanded: PropTypes.bool,
  updateCustomerStatus: PropTypes.func.isRequired,
  notify: PropTypes.func,
};

CustomerAccordion.defaultProps = {
  defaultExpanded: false,
  notify: undefined,
};
